import { Box, Button, Typography } from '@montugroup/design-system';
import { styled, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';

const SignatureFormControl = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 0,
  overflow: 'hidden',
  height: '150px',
  width: '100%',
  marginBottom: theme.spacing(4),
  canvas: {
    height: '100%',
    width: '100%',
    backgroundColor: theme.palette.common.white,
  },
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const SignatureFormGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(4),
}));

const TextButton = styled(Button)(({ theme }) => ({
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  padding: 0,
  alignSelf: 'flex-end',
}));

const Label = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

interface SignatureCanvasProps<T extends FieldValues> {
  fieldName: FieldPath<T>;
  label?: string | ReactNode;
  control: Control<T>;
  signatureDisclaimer?: string | ReactNode;
  clearSignatureText?: string;
}

export const ControlledSignaturePad = <T extends FieldValues>(props: SignatureCanvasProps<T>) => {
  const { fieldName, control, label, signatureDisclaimer, clearSignatureText = 'Clear signature' } = props;
  const theme = useTheme();
  const signatureRef = useRef<SignatureCanvas | null>(null);

  const formatSignature = () => {
    if (signatureRef.current) {
      return signatureRef.current.getTrimmedCanvas().toDataURL('image.png');
    }
    return null;
  };

  return (
    <Box>
      <Controller<T>
        name={fieldName}
        control={control}
        rules={{ required: 'Please sign here' }}
        render={({ field, fieldState }) => {
          const clearSignature = () => {
            if (signatureRef.current) {
              signatureRef.current.clear();
              field.onChange('');
            }
          };

          const hasError = Boolean(fieldState.error?.message);

          return (
            <SignatureFormGroup>
              {label && <Label>{label}</Label>}
              {signatureDisclaimer}
              <SignatureFormControl
                className="form-control"
                data-hasError={hasError}
                sx={hasError ? { borderColor: `${theme.palette?.error.main}!important` } : {}}
              >
                <SignatureCanvas
                  ref={signatureRef}
                  penColor="green"
                  backgroundColor={theme.palette.common.white}
                  onBegin={() => field.onChange(formatSignature())}
                  onEnd={() => field.onChange(formatSignature())}
                />
              </SignatureFormControl>
              {fieldState.error?.message ? (
                <Typography sx={{ color: 'error.main', marginBottom: '1rem', marginTop: '-1rem' }}>
                  {fieldState.error.message}
                </Typography>
              ) : null}
              <TextButton onClick={clearSignature} sx={{ fontSize: '1rem' }}>
                {clearSignatureText}
              </TextButton>
            </SignatureFormGroup>
          );
        }}
      />
    </Box>
  );
};
